import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, api } from "../context/AppContext";
import Navbar from "../components/Navbar";
import Breadcrumbs from "../components/Breadcrumbs";
import toast from "react-hot-toast";

/* ── load Razorpay SDK on demand ── */
const loadRazorpaySDK = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

/* ── style helpers ── */
const inputStyle = (err) => ({
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  borderRadius: 12,
  fontSize: 13.5,
  color: "#111827",
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  border: `1.5px solid ${err ? "#fca5a5" : "rgba(45,139,78,0.2)"}`,
  background: err ? "#fff5f5" : "#fff",
});
const onFocus = (e) => {
  e.target.style.borderColor = "#2D8B4E";
  e.target.style.boxShadow = "0 0 0 3px rgba(45,139,78,0.1)";
};
const onBlur = (err, e) => {
  e.target.style.borderColor = err ? "#fca5a5" : "rgba(45,139,78,0.2)";
  e.target.style.boxShadow = "none";
};

const Label = ({ children, required }) => (
  <label
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 700,
      color: "#6b7280",
      marginBottom: 5,
      textTransform: "uppercase",
      letterSpacing: ".5px",
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
);
const FieldError = ({ msg }) =>
  msg ? (
    <p style={{ fontSize: 11, color: "#ef4444", margin: "4px 0 0" }}>{msg}</p>
  ) : null;

const SectionCard = ({ icon, title, children }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 20,
      padding: 24,
      border: "1px solid #f0f0f0",
      boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    }}
  >
    <h2
      style={{
        fontSize: 15,
        fontWeight: 800,
        color: "#111827",
        margin: "0 0 18px",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: "rgba(45,139,78,0.1)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
        }}
      >
        {icon}
      </span>
      {title}
    </h2>
    {children}
  </div>
);

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user, cart, cartTotal, deliveryFee, clearCart } = useApp();

  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone?.replace("+91", "") || "",
    email: user?.email || "",
    street: user?.address?.street || "",
    city: user?.address?.city || "",
    state: user?.address?.state || "Karnataka",
    pincode: user?.address?.pincode || "",
    landmark: user?.address?.landmark || "",
  });

  const total = cartTotal + deliveryFee;

  const breadcrumbs = [
    { label: "Home", path: "/" },
    { label: "Products", path: "/products" },
    { label: "Cart", path: "/cart" },
    { label: "Checkout", path: null },
  ];

  const setField = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone || form.phone.replace(/\D/g, "").length < 10)
      e.phone = "Valid 10-digit phone required";
    if (!form.street.trim()) e.street = "Street address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.pincode || form.pincode.replace(/\D/g, "").length < 6)
      e.pincode = "Valid 6-digit pincode required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const customerDetails = () => ({
    name: form.name.trim(),
    phone: form.phone.startsWith("+91") ? form.phone : `+91${form.phone}`,
    email: form.email.trim() || undefined,
    address: {
      street: form.street.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      landmark: form.landmark.trim() || undefined,
    },
  });

  /* ══ COD ══ */
  const handleCOD = async () => {
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        items: cart.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
        })),
        customerDetails: customerDetails(),
        paymentMethod: "cod",
      });
      clearCart();
      toast.success("Order placed! We'll deliver within 2 hours 🚀");
      navigate(`/order-success/${res.data.order._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  /* ══ Razorpay ══ */
  const handleRazorpay = async () => {
    setLoading(true);
    try {
      /* 1. Create ProteinSpot order (pending) */
      const orderRes = await api.post("/orders", {
        items: cart.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
        })),
        customerDetails: customerDetails(),
        paymentMethod: "razorpay",
      });
      const psOrder = orderRes.data.order;

      /* 2. Create Razorpay order */
      const rzpRes = await api.post("/payments/create-order", {
        amount: total,
        receipt: `ps_${psOrder.orderId}`,
        notes: { psOrderId: psOrder._id, orderId: psOrder.orderId },
      });
      const { razorpayOrderId, keyId } = rzpRes.data;

      /* 3. Load SDK */
      const sdkLoaded = await loadRazorpaySDK();
      if (!sdkLoaded) {
        toast.error(
          "Could not load Razorpay. Check your internet and try again.",
        );
        setLoading(false);
        return;
      }

      /* 4. Open Razorpay popup */
      const rzp = new window.Razorpay({
        key: keyId,
        amount: Math.round(total * 100),
        currency: "INR",
        name: "ProteinSpot",
        description: `Order #${psOrder.orderId}`,
        image:
          "https://res.cloudinary.com/proteinspot/image/upload/v1774009667/logo-transparent_tsfxmf.png",
        order_id: razorpayOrderId,
        prefill: {
          name: form.name,
          email: form.email || undefined,
          contact: `+91${form.phone}`,
        },
        theme: { color: "#2D8B4E" },

        /* 5. On payment success → verify → navigate */
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              psOrderId: psOrder._id,
            });
            clearCart();
            toast.success("Payment successful! Order confirmed 🎉");
            navigate(`/order-success/${psOrder._id}`);
          } catch {
            /* payment went through but verification call failed (edge case) */
            toast(
              "Payment received. If order isn't confirmed, contact support with ID: " +
                response.razorpay_payment_id,
              { icon: "ℹ️", duration: 6000 },
            );
            clearCart();
            navigate(`/order-success/${psOrder._id}`);
          }
        },

        modal: {
          ondismiss: () => {
            /* user closed popup — order stays in DB as pending */
            toast(
              "Payment cancelled. Your order is saved — retry from My Orders.",
              { icon: "ℹ️" },
            );
            setLoading(false);
          },
        },
      });

      rzp.on("payment.failed", (res) => {
        toast.error(
          `Payment failed: ${res.error?.description || "Unknown error"}`,
        );
        setLoading(false);
      });

      rzp.open();
    } catch (err) {
      console.error("Razorpay flow error:", err);
      toast.error(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!validate()) {
      toast.error("Please fill all required fields");
      return;
    }
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (paymentMethod === "cod") await handleCOD();
    else await handleRazorpay();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafb",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      <Navbar />

      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "0 20px 60px",
          boxSizing: "border-box",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 pt-3 pb-0">
          <Breadcrumbs items={breadcrumbs} />
        </div>

        <h1
          style={{
            fontSize: 26,
            fontWeight: 900,
            color: "#111827",
            margin: "18px 0 24px",
            letterSpacing: "-0.4px",
          }}
        >
          Checkout
        </h1>

        <div className="co-grid">
          {/* ═══ LEFT ═══ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Delivery */}
            <SectionCard icon="🚚" title="Delivery Details">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                }}
              >
                <div>
                  <Label required>Full Name</Label>
                  <input
                    value={form.name}
                    onChange={setField("name")}
                    placeholder="Your full name"
                    style={inputStyle(errors.name)}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(errors.name, e)}
                  />
                  <FieldError msg={errors.name} />
                </div>

                <div>
                  <Label required>Phone Number</Label>
                  <div style={{ position: "relative" }}>
                    <span
                      style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 13,
                        color: "#6b7280",
                        fontWeight: 700,
                        pointerEvents: "none",
                      }}
                    >
                      +91
                    </span>
                    <input
                      value={form.phone}
                      onChange={(e) =>
                        setField("phone")({
                          target: {
                            value: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          },
                        })
                      }
                      placeholder="9876543210"
                      style={{ ...inputStyle(errors.phone), paddingLeft: 40 }}
                      onFocus={onFocus}
                      onBlur={(e) => onBlur(errors.phone, e)}
                    />
                  </div>
                  <FieldError msg={errors.phone} />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <Label>Email (for receipt)</Label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="you@email.com"
                    style={inputStyle(false)}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(false, e)}
                  />
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <Label required>Street Address</Label>
                  <textarea
                    value={form.street}
                    onChange={setField("street")}
                    placeholder="Flat no, Building name, Street…"
                    rows={2}
                    style={{ ...inputStyle(errors.street), resize: "none" }}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(errors.street, e)}
                  />
                  <FieldError msg={errors.street} />
                </div>

                <div>
                  <Label required>City</Label>
                  <input
                    value={form.city}
                    onChange={setField("city")}
                    placeholder="Bengaluru"
                    style={inputStyle(errors.city)}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(errors.city, e)}
                  />
                  <FieldError msg={errors.city} />
                </div>

                <div>
                  <Label>State</Label>
                  <input
                    value={form.state}
                    onChange={setField("state")}
                    placeholder="Karnataka"
                    style={inputStyle(false)}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(false, e)}
                  />
                </div>

                <div>
                  <Label required>Pincode</Label>
                  <input
                    value={form.pincode}
                    onChange={(e) =>
                      setField("pincode")({
                        target: {
                          value: e.target.value.replace(/\D/g, "").slice(0, 6),
                        },
                      })
                    }
                    placeholder="560038"
                    style={inputStyle(errors.pincode)}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(errors.pincode, e)}
                  />
                  <FieldError msg={errors.pincode} />
                </div>

                <div>
                  <Label>Landmark</Label>
                  <input
                    value={form.landmark}
                    onChange={setField("landmark")}
                    placeholder="Near…"
                    style={inputStyle(false)}
                    onFocus={onFocus}
                    onBlur={(e) => onBlur(false, e)}
                  />
                </div>
              </div>
            </SectionCard>

            {/* Payment */}
            <SectionCard icon="💳" title="Payment Method">
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {/* Razorpay */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    padding: "16px",
                    borderRadius: 14,
                    border: `2px solid ${paymentMethod === "razorpay" ? "#2D8B4E" : "#e5e7eb"}`,
                    background:
                      paymentMethod === "razorpay"
                        ? "rgba(45,139,78,0.04)"
                        : "#fff",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="radio"
                    name="pay"
                    value="razorpay"
                    checked={paymentMethod === "razorpay"}
                    onChange={() => setPaymentMethod("razorpay")}
                    style={{
                      accentColor: "#2D8B4E",
                      marginTop: 3,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ fontSize: 20 }}>💳</span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: "#111827",
                        }}
                      >
                        Pay Online
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          padding: "2px 8px",
                          borderRadius: 100,
                          background: "rgba(45,139,78,0.1)",
                          color: "#2D8B4E",
                          border: "1px solid rgba(45,139,78,0.25)",
                        }}
                      >
                        Recommended
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#9ca3af",
                        margin: "0 0 10px",
                      }}
                    >
                      UPI · Cards · Net Banking · Wallets — secured by Razorpay
                    </p>
                    {/* accepted methods */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {[
                        "UPI",
                        "GPay",
                        "PhonePe",
                        "Paytm",
                        "Visa",
                        "Mastercard",
                        "Rupay",
                        "Net Banking",
                      ].map((m) => (
                        <span
                          key={m}
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            color: "#6b7280",
                          }}
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                  {paymentMethod === "razorpay" && (
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "#2D8B4E",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </span>
                  )}
                </label>

                {/* COD */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "16px",
                    borderRadius: 14,
                    border: `2px solid ${paymentMethod === "cod" ? "#2D8B4E" : "#e5e7eb"}`,
                    background:
                      paymentMethod === "cod" ? "rgba(45,139,78,0.04)" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <input
                    type="radio"
                    name="pay"
                    value="cod"
                    checked={paymentMethod === "cod"}
                    onChange={() => setPaymentMethod("cod")}
                    style={{ accentColor: "#2D8B4E", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 20 }}>💵</span>
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: "#111827",
                        margin: 0,
                      }}
                    >
                      Cash on Delivery
                    </p>
                    <p
                      style={{
                        fontSize: 12,
                        color: "#9ca3af",
                        margin: "2px 0 0",
                      }}
                    >
                      Pay in cash when your order arrives at your door
                    </p>
                  </div>
                  {paymentMethod === "cod" && (
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "#2D8B4E",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </span>
                  )}
                </label>
              </div>

              {/* SSL note */}
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 14px",
                  background: "rgba(45,139,78,0.04)",
                  borderRadius: 10,
                  border: "1px solid rgba(45,139,78,0.1)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 16 }}>🔒</span>
                <p style={{ fontSize: 11, color: "#6b7280", margin: 0 }}>
                  Your payment information is encrypted with 256-bit SSL.
                  {paymentMethod === "razorpay"
                    ? " Powered by Razorpay — PCI DSS Level 1 certified."
                    : " No card details required for COD."}
                </p>
              </div>
            </SectionCard>
          </div>

          {/* ═══ RIGHT — summary ═══ */}
          <div>
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: 22,
                border: "1px solid #f0f0f0",
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                position: "sticky",
                top: 80,
              }}
            >
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: 800,
                  color: "#111827",
                  margin: "0 0 16px",
                }}
              >
                Order Summary
              </h2>

              {/* items */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginBottom: 16,
                }}
              >
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        objectFit: "cover",
                        flexShrink: 0,
                        border: "1px solid #f0f0f0",
                      }}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/40/e8f5e9/2D8B4E?text=🥤";
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: "#111827",
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.name}
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          margin: "1px 0 0",
                        }}
                      >
                        ×{item.quantity} · ₹{item.price} each
                      </p>
                    </div>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#374151",
                        flexShrink: 0,
                      }}
                    >
                      ₹{item.price * item.quantity}
                    </span>
                  </div>
                ))}
              </div>

              {/* bill */}
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  paddingTop: 14,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ fontSize: 13, color: "#6b7280" }}>
                    Subtotal ({cart.reduce((s, i) => s + i.quantity, 0)} items)
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    ₹{cartTotal}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 13, color: "#6b7280" }}>
                    Delivery
                  </span>
                  {deliveryFee === 0 ? (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#16a34a",
                        background: "rgba(22,163,74,0.1)",
                        padding: "2px 10px",
                        borderRadius: 100,
                      }}
                    >
                      FREE
                    </span>
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      ₹{deliveryFee}
                    </span>
                  )}
                </div>
                {deliveryFee > 0 && (
                  <p
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      margin: "-4px 0 0",
                    }}
                  >
                    Add ₹{299 - cartTotal} more for free delivery
                  </p>
                )}
              </div>

              <div
                style={{
                  borderTop: "1.5px solid #f0f0f0",
                  margin: "14px 0",
                  paddingTop: 14,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontSize: 16, fontWeight: 900, color: "#111827" }}
                >
                  Total
                </span>
                <span
                  style={{
                    fontSize: 24,
                    fontWeight: 900,
                    color: "#15803d",
                    letterSpacing: "-0.5px",
                  }}
                >
                  ₹{total}
                </span>
              </div>

              {/* CTA */}
              <button
                onClick={handlePlaceOrder}
                disabled={loading || cart.length === 0}
                style={{
                  width: "100%",
                  padding: "14px",
                  borderRadius: 14,
                  border: "none",
                  background: loading
                    ? "#d1d5db"
                    : "linear-gradient(135deg,#15803d,#22c55e)",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 800,
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading
                    ? "none"
                    : "0 4px 20px rgba(21,128,61,0.35)",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 26px rgba(21,128,61,0.45)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = loading
                    ? "none"
                    : "0 4px 20px rgba(21,128,61,0.35)";
                }}
              >
                {loading ? (
                  <>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.65s linear infinite",
                        display: "inline-block",
                      }}
                    />
                    {paymentMethod === "razorpay"
                      ? "Opening payment…"
                      : "Placing order…"}
                  </>
                ) : paymentMethod === "razorpay" ? (
                  `💳 Pay ₹${total} Online`
                ) : (
                  `✅ Place Order — ₹${total}`
                )}
              </button>

              <p
                style={{
                  fontSize: 11,
                  color: "#9ca3af",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                {paymentMethod === "razorpay"
                  ? "🔒 256-bit SSL · PCI DSS compliant · Secured by Razorpay"
                  : "💵 Pay cash on delivery · No advance required"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .co-grid {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 20px;
          align-items: start;
        }
        @media (max-width: 768px) {
          .co-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}
